import { gql } from '@apollo/client';
import type {
  CustodianActivity,
  CustodianActivityMetrics,
  FetchCustodianActivitiesInputForGrid,
  FetchCustodianActivityMetricsInput,
} from '@generated/graphql';

export const FETCH_CUSTODIAN_ACTIVITY_METRICS = gql`
  query fetchCustodianActivityMetrics($input: FetchCustodianActivityMetricsInput!) {
    fetchCustodianActivityMetrics(input: $input) {
      metrics {
        createdCount
        reconciledTodayCount
        agingPendingCount
        occurringTodayCount
      }
    }
  }
`;

/** Metric counts selected by `FETCH_CUSTODIAN_ACTIVITY_METRICS`. */
export type CustodianActivityMetricsCounts = Pick<
  CustodianActivityMetrics,
  | 'createdCount'
  | 'reconciledTodayCount'
  | 'agingPendingCount'
  | 'occurringTodayCount'
>;

export type FetchCustodianActivityMetricsQuery = {
  fetchCustodianActivityMetrics: {
    metrics: CustodianActivityMetricsCounts;
  };
};

export type FetchCustodianActivityMetricsQueryVariables = {
  input: FetchCustodianActivityMetricsInput;
};

/**
 * Worklist query — selects table columns plus fields reserved for later
 * Details / Notes / Account tab phases to avoid selection churn.
 */
export const FETCH_CUSTODIAN_ACTIVITIES_FOR_GRID = gql`
  query fetchCustodianActivitiesForGrid($input: FetchCustodianActivitiesInputForGrid!) {
    fetchCustodianActivitiesForGrid(input: $input) {
      totalCount
      custodianActivities {
        id
        clientName
        custodianAccountNumber
        processDate
        effectiveDate
        settleDate
        transactionType
        transactionDesc
        transactionId
        subTransactionId
        amountValueCents
        quantity
        priceCents
        currency
        symbol
        cusip
        state
        reconcileMethod
        reconciledAt
        notes {
          text
          author
          authorId
          createdAt
        }
        account {
          id
          type
          custodianAccountNumber
          organization {
            id
            translatedName {
              en
              fr
              es
            }
          }
          user {
            id
            firstName
            lastName
            entityName
          }
        }
        financialProduct {
          id
          ticker
          currency
          translatedName {
            en
            fr
            es
          }
        }
      }
    }
  }
`;

export type ActivitiesGridActivity = Pick<
  CustodianActivity,
  | 'id'
  | 'clientName'
  | 'custodianAccountNumber'
  | 'processDate'
  | 'effectiveDate'
  | 'settleDate'
  | 'transactionType'
  | 'transactionDesc'
  | 'transactionId'
  | 'subTransactionId'
  | 'amountValueCents'
  | 'quantity'
  | 'priceCents'
  | 'currency'
  | 'symbol'
  | 'cusip'
  | 'state'
  | 'reconcileMethod'
  | 'reconciledAt'
  | 'notes'
> & {
  account?: {
    id: string;
    type: string;
    custodianAccountNumber?: string | null;
    organization?: {
      id: string;
      translatedName?: {
        en?: string | null;
        fr?: string | null;
        es?: string | null;
      } | null;
    } | null;
    user?: {
      id: string;
      firstName?: string | null;
      lastName?: string | null;
      entityName?: string | null;
    } | null;
  } | null;
  financialProduct?: {
    id: string;
    ticker?: string | null;
    currency?: string | null;
    translatedName?: {
      en?: string | null;
      fr?: string | null;
      es?: string | null;
    } | null;
  } | null;
};

export type FetchCustodianActivitiesForGridQuery = {
  fetchCustodianActivitiesForGrid: {
    totalCount: number;
    custodianActivities: ActivitiesGridActivity[];
  };
};

export type FetchCustodianActivitiesForGridQueryVariables = {
  input: FetchCustodianActivitiesInputForGrid;
};
